import {
    normalizeFetchAttemptsAnalyticsResponse,
    normalizeFetchCategoriesDistributionByQuestionsAnalyticsResponse,
    normalizeFetchConfidenceDistributionAnalyticsResponse,
    normalizeFetchDistributionBetweenPhasesAnalyticsResponse,
    normalizeFetchGradesPerComponentAnalyticsResponse,
    normalizeFetchGroupsAnalyticsResponse,
    normalizeFetchQuestionRubricsApproveAnalyticsResponse,
    normalizeFetchReviewersApproveAnalyticsResponse,
    normalizeFetchUnitTestsAnalyticsResponse,
    normalizeFetchEstimateRemainingReviewTimeResponse,
    normalizeFetchAiAcceptanceRateAnalyticsResponse,
    normalizeFetchFeedbackModificationStatsResponse,
} from './utils/normalizers/analytics.normalizers'
import baseApi from './base.api'
import { ANSWER_CATEGORY_TAG, APPROVE_ANALYTICS_TAG, ASSIGNMENT_TAG, FEEDBACK_MODIFICATION_TAG, GROUP_TAG, SUBMISSION_TAG } from './utils/tags'

export const analyticsApi = baseApi.injectEndpoints({
    endpoints: builder => ({
        fetchGradesPerComponentAnalytics: builder.query({
            query: ({ token }) => ({
                url: `/reports/grades-per-component/${token}`,
                method: 'GET',
            }),
            transformResponse: normalizeFetchGradesPerComponentAnalyticsResponse,
        }),
        fetchAttemptsAnalytics: builder.query({
            query: ({ token }) => ({
                url: `/reports/number-of-attempts/${token}`,
                method: 'GET',
            }),
            transformResponse: normalizeFetchAttemptsAnalyticsResponse,
        }),
        fetchGroupsAnalytics: builder.query({
            query: ({ token }) => ({
                url: `/reports/students-per-group/${token}`,
                method: 'GET',
            }),
            transformResponse: normalizeFetchGroupsAnalyticsResponse,
            providesTags: [{ type: GROUP_TAG, id: 'LIST' }],
        }),
        fetchUnitTestsAnalytics: builder.query({
            query: ({ token }) => ({
                url: `/reports/ut-results/${token}`,
                method: 'GET',
            }),
            transformResponse: normalizeFetchUnitTestsAnalyticsResponse,
        }),
        fetchCategoriesDistributionByQuestionsAnalytics: builder.query({
            query: ({ token }) => ({
                url: `/free-text-statistics/${token}/pattern-distribution-by-questions`,
                method: 'GET',
            }),
            transformResponse: normalizeFetchCategoriesDistributionByQuestionsAnalyticsResponse,
            providesTags: [{ type: ANSWER_CATEGORY_TAG, id: 'LIST' }, SUBMISSION_TAG, ASSIGNMENT_TAG],
        }),
        fetchDistributionBetweenPhasesAnalytics: builder.query({
            query: ({ token }) => ({
                url: `/free-text-statistics/${token}/pattern-distribution-between-model-phases`,
                method: 'GET',
            }),
            transformResponse: normalizeFetchDistributionBetweenPhasesAnalyticsResponse,
            providesTags: [{ type: ANSWER_CATEGORY_TAG, id: 'LIST' }, SUBMISSION_TAG, ASSIGNMENT_TAG],
        }),
        fetchConfidenceDistributionAnalytics: builder.query({
            query: ({ token }) => ({
                url: `/free-text-statistics/${token}/confidence-distribution`,
                method: 'GET',
            }),
            transformResponse: normalizeFetchConfidenceDistributionAnalyticsResponse,
            providesTags: [ASSIGNMENT_TAG],
        }),
        fetchQuestionRubricsApproveAnalytics: builder.query({
            query: ({ token, assignee }) => ({
                url: `/rubrics/question-rubrics/${token}/approve-diagram`,
                method: 'GET',
                params: {
                    assignee,
                },
            }),
            transformResponse: normalizeFetchQuestionRubricsApproveAnalyticsResponse,
            providesTags: [ASSIGNMENT_TAG, SUBMISSION_TAG, ANSWER_CATEGORY_TAG, APPROVE_ANALYTICS_TAG],
        }),
        fetchReviewersApproveAnalytics: builder.query({
            query: ({ token }) => ({
                url: `/free-text-statistics/${token}/reviewers-progress`,
                method: 'GET',
            }),
            transformResponse: normalizeFetchReviewersApproveAnalyticsResponse,
            providesTags: [ASSIGNMENT_TAG, SUBMISSION_TAG, ANSWER_CATEGORY_TAG, APPROVE_ANALYTICS_TAG],
        }),
        fetchEstimateRemainingReviewTime: builder.query({
            query: ({ token }) => ({
                url: `/assignment/${token}/estimate-remaining-review-time`,
                method: 'GET',
            }),
            transformResponse: normalizeFetchEstimateRemainingReviewTimeResponse,
            providesTags: [ASSIGNMENT_TAG, SUBMISSION_TAG],
        }),
        fetchAiAcceptanceRateAnalytics: builder.query({
            query: ({ token, include_auto_approved = true }) => ({
                url: `/free-text-statistics/${token}/ai-acceptance-rate?include_auto_approved=${include_auto_approved}`,
                method: 'GET',
            }),
            transformResponse: normalizeFetchAiAcceptanceRateAnalyticsResponse,
            providesTags: [ASSIGNMENT_TAG, SUBMISSION_TAG, ANSWER_CATEGORY_TAG, APPROVE_ANALYTICS_TAG],
        }),
        exportConfidenceDistribution: builder.query({
            query: ({ token, format = 'csv' }) => ({
                url: `/free-text-statistics/${token}/export/confidence-distribution?format=${format}`,
                method: 'GET',
                responseType: 'blob',
            }),
        }),
        exportDistributionBetweenPhases: builder.query({
            query: ({ token, phasesToShow }) => {
                const params = {}
                if (phasesToShow) {
                    if (!phasesToShow.includes('Historical')) params.include_historical = false
                    if (!phasesToShow.includes('Predicted')) params.include_predicted = false
                    if (!phasesToShow.includes('Human-reviewed')) params.include_human_reviewed = false
                }
                return {
                    url: `/free-text-statistics/${token}/export/distribution-between-phases`,
                    method: 'GET',
                    responseType: 'blob',
                    params,
                }
            },
        }),
        exportAiAcceptanceRate: builder.query({
            query: ({ token, format = 'csv', include_auto_approved = true }) => ({
                url: `/free-text-statistics/${token}/export/ai-acceptance-rate?format=${format}&include_auto_approved=${include_auto_approved}`,
                method: 'GET',
                responseType: 'blob',
            }),
        }),
        fetchFeedbackModificationStats: builder.query({
            query: ({ token }) => ({
                url: `/free-text-statistics/${token}/feedback-modification-stats`,
                method: 'GET',
            }),
            transformResponse: normalizeFetchFeedbackModificationStatsResponse,
            providesTags: [ASSIGNMENT_TAG, SUBMISSION_TAG, ANSWER_CATEGORY_TAG, FEEDBACK_MODIFICATION_TAG],
        }),
        exportFeedbackModificationStats: builder.query({
            query: ({ token, format = 'csv' }) => ({
                url: `/free-text-statistics/${token}/export/feedback-modification-stats?format=${format}`,
                method: 'GET',
                responseType: 'blob',
            }),
        }),
    }),
})

export const {
    useFetchGradesPerComponentAnalyticsQuery,
    useFetchAttemptsAnalyticsQuery,
    useFetchGroupsAnalyticsQuery,
    useFetchUnitTestsAnalyticsQuery,
    useFetchCategoriesDistributionByQuestionsAnalyticsQuery,
    useFetchDistributionBetweenPhasesAnalyticsQuery,
    useFetchConfidenceDistributionAnalyticsQuery,
    useFetchQuestionRubricsApproveAnalyticsQuery,
    useFetchReviewersApproveAnalyticsQuery,
    useFetchEstimateRemainingReviewTimeQuery,
    useFetchAiAcceptanceRateAnalyticsQuery,
    useLazyExportConfidenceDistributionQuery,
    useLazyExportDistributionBetweenPhasesQuery,
    useLazyExportAiAcceptanceRateQuery,
    useFetchFeedbackModificationStatsQuery,
    useLazyExportFeedbackModificationStatsQuery,
} = analyticsApi
